import React, { useState, useEffect, useRef } from 'react';
import { ArrowRight, ChevronDown } from 'lucide-react';

const HeroSection = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [currentVideo, setCurrentVideo] = useState(0);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const videoRefs = useRef([]);
  
  const headlines = [
    { main: 'Next Gen', sub: 'Multi-Ion' },
    { main: 'Industrial', sub: 'Traction' },
    { main: 'Energy', sub: 'Storage' },
    { main: 'Grid Scale', sub: 'Power' },
  ];

  // Video/Image options - optimized for faster loading
  const videos = [
    {
      src: 'https://videos.pexels.com/video-files/31352807/13380607_2560_1440_25fps.mp4',
      poster: 'https://images.pexels.com/videos/31352807/pexels-photo-31352807.jpeg?auto=compress&cs=tinysrgb&w=800',
      label: 'Traction',
      type: 'video',
      alt: 'Forklift traction battery application',
    },
    {
      src: 'https://videos.pexels.com/video-files/32386621/13814766_2560_1440_100fps.mp4',
      poster: 'https://images.pexels.com/videos/32386621/pexels-photo-32386621.jpeg?auto=compress&cs=tinysrgb&w=800',
      label: 'Industrial',
      type: 'video',
      alt: 'Industrial battery manufacturing',
    },
    {
      src: 'https://videos.pexels.com/video-files/30915324/13218355_2560_1440_60fps.mp4',
      poster: 'https://images.pexels.com/videos/30915324/heating-plant-power-plant-power-transmission-30915324.jpeg?auto=compress&cs=tinysrgb&w=800',
      label: 'Energy Storage',
      type: 'video',
      alt: 'Energy storage system grid infrastructure',
    },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % headlines.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [headlines.length]);

  // Video rotation with optimized loading
  useEffect(() => {
    const videoInterval = setInterval(() => {
      setCurrentVideo((prev) => (prev + 1) % videos.length);
    }, 10000);
    return () => clearInterval(videoInterval);
  }, [videos.length]);

  // Lazy load videos when they become active
  useEffect(() => {
    const video = videoRefs.current[currentVideo];
    if (video && video.paused) {
      video.play().catch(() => {});
    }
  }, [currentVideo]);

  const scrollToNext = () => {
    const nextSection = document.getElementById('how-multiion-works');
    if (nextSection) {
      nextSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section 
      className="relative min-h-screen flex items-center overflow-hidden" 
      data-testid="hero-section" 
      aria-label="Gegadyne Energy - Industrial Battery Systems"
      itemScope 
      itemType="https://schema.org/WebPageElement"
    >
      {/* Background Videos with Lazy Loading */}
      <div className="absolute inset-0 z-0">
        {videos.map((video, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              index === currentVideo ? 'opacity-100' : 'opacity-0'
            }`}
            aria-hidden="true"
          >
            {/* Poster image as fallback and initial load */}
            <img
              src={video.poster}
              alt=""
              className={`absolute inset-0 w-full h-full object-cover ${
                videoLoaded && index === currentVideo ? 'opacity-0' : 'opacity-100'
              } transition-opacity duration-500`}
              loading={index === 0 ? 'eager' : 'lazy'}
              fetchpriority={index === 0 ? 'high' : 'auto'}
              decoding="async"
              width="1920"
              height="1080"
            />
            {video.type === 'video' && (
              <video
                ref={(el) => (videoRefs.current[index] = el)}
                autoPlay={index === currentVideo}
                muted
                loop
                playsInline
                preload={index === 0 ? 'auto' : 'none'}
                className="absolute inset-0 w-full h-full object-cover"
                poster={video.poster}
                onLoadedData={() => index === currentVideo && setVideoLoaded(true)}
              >
                <source src={video.src} type="video/mp4" />
              </video>
            )}
          </div>
        ))}
        {/* Overlays */}
        <div className="absolute inset-0 bg-gray-950/70" />
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-900/30 via-transparent to-cyan-900/20" />
        <div className="absolute bottom-0 left-0 right-0 h-32 sm:h-48 bg-gradient-to-t from-gray-950 to-transparent" />
        <div className="absolute left-0 top-1/3 w-48 sm:w-96 h-48 sm:h-96 bg-emerald-500/10 blur-[100px] sm:blur-[150px] rounded-full" />
      </div>

      {/* Content - Mobile First */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 sm:pt-28 lg:pt-32 pb-24 sm:pb-16 lg:pb-20 w-full">
        <div className="max-w-5xl">
          {/* Animated Headlines - Responsive sizing */}
          <div className="relative h-[140px] sm:h-[200px] md:h-[280px] lg:h-[320px] mb-6 sm:mb-8 overflow-hidden">
            {headlines.map((headline, index) => (
              <div
                key={index}
                className={`absolute inset-0 transition-all duration-700 ease-out ${
                  index === currentSlide 
                    ? 'opacity-100 translate-y-0' 
                    : index < currentSlide 
                      ? 'opacity-0 -translate-y-full' 
                      : 'opacity-0 translate-y-full'
                }`}
              >
                <h1 className="text-4xl sm:text-6xl md:text-7xl lg:text-8xl xl:text-9xl font-black text-white leading-[0.9] tracking-tight uppercase">
                  <span className="block">{headline.main}</span>
                  <span className="block bg-gradient-to-r from-emerald-400 via-cyan-400 to-emerald-400 bg-clip-text text-transparent">
                    {headline.sub}
                  </span>
                </h1>
              </div>
            ))}
          </div>

          {/* SEO H2 - Hidden visually but crawlable */}
          <h2 className="sr-only">
            Industrial Battery Manufacturer India - Traction Batteries, Energy Storage Systems, Ground Power Units
          </h2>
          
          {/* Description - Mobile optimized */}
          <p className="text-base sm:text-lg lg:text-xl xl:text-2xl text-gray-300 mb-3 sm:mb-4 max-w-3xl leading-relaxed">
            Rugged Multi-Ion battery systems for industrial and infrastructure applications that operate under constant load and adverse conditions.
          </p>
          <p className="text-sm sm:text-base lg:text-lg text-gray-400 mb-6 sm:mb-10 max-w-3xl leading-relaxed">
            Designed for fast recharge, long service life, and dependable performance. Our traction batteries power forklifts, cranes, and warehouse equipment. Energy storage systems provide backup power and grid-scale support across India.
          </p>

          {/* Application category pills - Mobile scrollable */}
          <nav 
            className="flex items-center gap-2 sm:gap-3 mb-6 sm:mb-10 overflow-x-auto pb-2 -mx-4 px-4 sm:mx-0 sm:px-0 sm:overflow-visible scrollbar-hide" 
            aria-label="Battery application categories"
          >
            {videos.map((video, index) => (
              <button
                key={index}
                onClick={() => setCurrentVideo(index)}
                className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-full text-xs sm:text-sm font-medium transition-all duration-300 whitespace-nowrap min-h-[44px] ${
                  index === currentVideo 
                    ? 'bg-emerald-500/20 border border-emerald-500/50 text-emerald-400' 
                    : 'bg-white/5 border border-white/10 text-gray-400 hover:bg-white/10 hover:text-white active:bg-white/15'
                }`}
                aria-pressed={index === currentVideo}
                aria-label={`View ${video.label} applications`}
              >
                <span className={`w-2 h-2 rounded-full flex-shrink-0 ${index === currentVideo ? 'bg-emerald-400' : 'bg-gray-500'}`} aria-hidden="true" />
                {video.label}
              </button>
            ))}
          </nav>

          {/* CTA Button - Mobile optimized tap target */}
          <a
            href="#contact"
            data-testid="hero-contact-cta"
            className="group inline-flex items-center justify-center gap-2 sm:gap-3 bg-emerald-500 hover:bg-emerald-400 active:bg-emerald-600 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg text-base sm:text-lg font-semibold transition-all duration-300 hover:shadow-xl hover:shadow-emerald-500/25 border border-emerald-400/50 min-h-[48px] w-full sm:w-auto"
          >
            Talk to Engineering
            <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform" aria-hidden="true" />
          </a>
        </div>
      </div>

      {/* Scroll indicator - Hidden on very small screens */}
      <button 
        onClick={scrollToNext}
        className="hidden sm:flex absolute bottom-20 sm:bottom-8 left-1/2 -translate-x-1/2 z-10 flex-col items-center gap-2 text-white/60 hover:text-white transition-colors cursor-pointer min-h-[44px] min-w-[44px] items-center justify-center"
        aria-label="Scroll to learn about Multi-Ion technology"
      >
        <ChevronDown className="w-6 h-6 sm:w-8 sm:h-8 animate-bounce" aria-hidden="true" />
      </button>

      {/* Stats Bar - Key specs with mobile responsive grid */}
      <aside className="absolute bottom-0 left-0 right-0 z-10 bg-gray-950/80 backdrop-blur-sm sm:bg-transparent sm:backdrop-blur-none" aria-label="Battery specifications">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:pb-8 sm:pt-0">
          <div className="grid grid-cols-3 gap-2 sm:flex sm:items-center sm:gap-8 lg:gap-16">
            {[
              { value: '5,000+', label: 'Cycles', fullLabel: 'Cycle Life' },
              { value: '15 min', label: 'Charge', fullLabel: 'Fast Charge' },
              { value: '-40° to 65°C', label: 'Temp', fullLabel: 'Operating Range' },
            ].map((stat, index) => (
              <div key={index} className="flex flex-col sm:flex-row items-center sm:items-baseline gap-0.5 sm:gap-2 text-center sm:text-left">
                <span className="text-lg sm:text-2xl lg:text-3xl font-bold text-emerald-400">{stat.value}</span>
                <span className="text-gray-500 text-[10px] sm:text-sm uppercase tracking-wider">
                  <span className="sm:hidden">{stat.label}</span>
                  <span className="hidden sm:inline">{stat.fullLabel}</span>
                </span>
              </div>
            ))}
          </div>
        </div>
      </aside>
    </section>
  );
};

export default HeroSection;
